import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Pause, Download, Music2 } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import { useTr } from "@/i18n";

const SRC = "/assets/mixes/euro-style.mp3";

function fmt(s) {
  if (!isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
}

export default function MixPlayer() {
  const tr = useTr();
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [dur, setDur] = useState(0);

  useEffect(() => {
    const audio = new Audio();
    audio.preload = "metadata"; // solo i metadati (durata): il mix da 3.2 MB si scarica premendo play
    audio.src = SRC;
    audioRef.current = audio;
    const onTime = () => setTime(audio.currentTime);
    const onMeta = () => setDur(audio.duration);
    const onEnd = () => setPlaying(false);
    const stop = () => {
      audio.pause();
      setPlaying(false);
    };
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onMeta);
    audio.addEventListener("ended", onEnd);
    window.addEventListener("dj-stop-audio", stop);
    return () => {
      audio.pause();
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onMeta);
      audio.removeEventListener("ended", onEnd);
      window.removeEventListener("dj-stop-audio", stop);
    };
  }, []);

  const toggle = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
      setPlaying(false);
      return;
    }
    window.dispatchEvent(new Event("dj-stop-audio"));
    try {
      await audio.play();
      setPlaying(true);
      trackEvent("mix_play", { track: "The Best of DJ Magix — Euro Style" });
    } catch {
      setPlaying(false);
    }
  };

  const seek = (e) => {
    const audio = audioRef.current;
    if (!audio || !dur) return;
    const r = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
    audio.currentTime = ratio * dur;
    setTime(ratio * dur);
  };

  const pct = dur ? (time / dur) * 100 : 0;

  return (
    <section id="mix" className="py-20 bg-[#080808]">
      <div className="max-w-4xl mx-auto px-6">
        <motion.div
          data-testid="mix-player-section"
          className="gold-frame rounded-2xl bg-[#111111] p-7 sm:p-9"
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="flex flex-col sm:flex-row items-center gap-7">
            <div className="relative shrink-0">
              <motion.img
                src="/assets/logo-256.png"
                alt="DJ Magix"
                width={112}
                height={112}
                className="h-28 w-28 rounded-full object-cover border border-[rgba(226,184,87,0.4)]"
                animate={playing ? { rotate: 360 } : { rotate: 0 }}
                transition={
                  playing
                    ? { repeat: Infinity, duration: 6, ease: "linear" }
                    : { duration: 0.6 }
                }
              />
              <span className="absolute inset-0 m-auto h-3.5 w-3.5 rounded-full bg-[#080808] border border-[rgba(226,184,87,0.5)]" />
            </div>

            <div className="flex-1 w-full text-center sm:text-left">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#BFA45D] flex items-center justify-center sm:justify-start gap-2">
                <Music2 size={12} /> {tr("mix.label", "L'ultimo mix — Free Download")}
              </p>
              <h2 className="font-display text-xl sm:text-2xl font-semibold text-[#F7DE8B] mt-2">
                The Best of DJ Magix
              </h2>
              <p className="font-accent italic text-base text-[#E2C882]/80">Euro Style</p>

              <div className="flex items-center gap-4 mt-5">
                <button
                  data-testid="mix-player-toggle"
                  onClick={toggle}
                  aria-label={playing ? tr("mix.pause", "Pausa") : tr("mix.play", "Riproduci")}
                  className="gold-btn flex h-14 w-14 shrink-0 items-center justify-center rounded-full"
                >
                  {playing ? <Pause size={22} /> : <Play size={22} className="ml-1" />}
                </button>
                <div className="flex-1">
                  <div
                    data-testid="mix-player-progress"
                    onClick={seek}
                    className="group relative h-2.5 w-full cursor-pointer rounded-full bg-[#080808] border border-[rgba(226,184,87,0.25)]"
                  >
                    <div
                      className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-[#9E7B28] to-[#F7DE8B]"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <div className="flex justify-between mt-2 text-xs text-[#9F8A52] tabular-nums">
                    <span data-testid="mix-player-time">{fmt(time)}</span>
                    <span>{fmt(dur)}</span>
                  </div>
                </div>
                <div className="hidden sm:flex items-end gap-1 h-8 shrink-0" aria-hidden="true">
                  {[...Array(8)].map((_, b) => (
                    <span
                      key={b}
                      className={`w-1 h-full rounded-sm bg-gradient-to-t from-[#9E7B28] to-[#F7DE8B] ${
                        playing ? "eq-bar" : "scale-y-[0.25] origin-bottom"
                      }`}
                      style={playing ? { animationDelay: `${b * 0.08}s` } : undefined}
                    />
                  ))}
                </div>
                <a
                  data-testid="mix-player-download"
                  href={SRC}
                  download="The-Best-of-DJ-Magix-Euro-Style.mp3"
                  aria-label={tr("mix.download", "Scarica il mix")}
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full gold-frame text-[#E2B857] hover:bg-[#E2B857]/10 transition-colors"
                >
                  <Download size={18} />
                </a>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
